import { useMutation } from "@tanstack/react-query";
import {
  createState,
  pluralize,
  service,
  useAuthConfig,
} from "@theprelude/core";
import {
  Button,
  Checkbox,
  CloseIcon,
  IconButton,
  Overlay,
  PlusIcon,
  TooltipArrow,
  TooltipContent,
  Tooltip,
  TooltipTrigger,
  TooltipProvider,
  notifyError,
  notifySuccess,
  Input,
} from "@theprelude/ds";
import { useState } from "react";

export type TagOverlayProps = {
  endpoints: string[];
  tags: string[];
  relevantTags: string[];
  sharedTags: string[];
};

export const useTagOverlayProps = createState<TagOverlayProps | null>(null);

const TagOverlay: React.FC<{ resetTable: () => void }> = ({ resetTable }) => {
  const tagOverlayProps = useTagOverlayProps((state) => state.value);
  if (!tagOverlayProps) {
    return null;
  }
  return <EndpointTagOverlay resetTable={resetTable} props={tagOverlayProps} />;
};

const EndpointTagOverlay: React.FC<{
  resetTable: () => void;
  props: TagOverlayProps;
}> = ({ resetTable, props }) => {
  const [selectedTags, setSelectedTags] = useState<string[]>(props.sharedTags);
  const [showTagCreator, setShowTagCreator] = useState<boolean>(false);
  const [createdTag, setCreatedTag] = useState<string>("");
  const [tagList, setTagList] = useState<string[]>(props.tags);
  const authConfig = useAuthConfig();
  const setTagOverlayProps = useTagOverlayProps((state) => state.setValue);

  const handleSelectedTags = (tag: string) => {
    setSelectedTags((tags) => {
      if (!tags) {
        return [tag];
      }
      if (!tags.includes(tag)) {
        return [...tags, tag];
      }

      const updatedTags = tags.filter((t) => t !== tag);
      if (updatedTags.length > 0) {
        return updatedTags;
      }

      return [];
    });
  };

  const addTag = useMutation({
    mutationFn: async () => {
      return Promise.all(
        props.endpoints.map((endpoint_id) =>
          service(authConfig).detect.updateEndpoint({
            endpoint_id,
            tags: selectedTags.join(","),
          }),
        ),
      );
    },
    onSuccess: async () => {
      notifySuccess({
        message: `Successfully added tags to ${
          props.endpoints.length
        } ${pluralize(props.endpoints.length, "endpoint")}`,
        dismissible: true,
      });

      setTagOverlayProps(null);
      resetTable();
    },
    onError: async (error) => {
      notifyError({
        message: "Error occurred while trying to add tags",
        error,
        dismissible: true,
      });
    },
  });

  return (
    <Overlay
      position={"right"}
      title={"Tag Endpoints"}
      hideOverlay={() => setTagOverlayProps(null)}
    >
      <div className="flex flex-col gap-4 border-t border-stroke-line p-4">
        <div>
          {tagList.map((tag) => (
            <div className="flex items-center gap-2 px-2 py-1" key={tag}>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center">
                      <Checkbox
                        id={tag}
                        value={tag}
                        checked={selectedTags?.includes(tag)}
                        onCheckedChange={() => handleSelectedTags(tag)}
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    {props.relevantTags?.includes(tag)
                      ? "This tag is  shared by all endpoints."
                      : "This tag is not shared by all endpoints."}
                    <TooltipArrow />
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <label htmlFor={tag}>{tag}</label>
            </div>
          ))}
          {showTagCreator ? (
            <form
              className="mx-2 my-1 w-60"
              onSubmit={() => {
                setTagList([...tagList, createdTag]);
                handleSelectedTags(createdTag);
                setShowTagCreator(false);
              }}
            >
              <Input
                placeholder="Add a tag"
                after={
                  createdTag ? (
                    <IconButton icon={<PlusIcon />} type="submit" />
                  ) : (
                    <IconButton
                      icon={<CloseIcon />}
                      onClick={() => setShowTagCreator(false)}
                    />
                  )
                }
                value={createdTag}
                onChange={(ev) => setCreatedTag(ev.target.value)}
                autoFocus={true}
              />
            </form>
          ) : (
            <span
              role="button"
              className="flex cursor-pointer items-center gap-2 px-2 py-1"
              onClick={() => {
                setCreatedTag("");
                setShowTagCreator(true);
              }}
            >
              <PlusIcon className="size-4" /> Add a tag
            </span>
          )}
        </div>

        <Button
          intent="primary"
          onClick={() => addTag.mutate()}
          disabled={selectedTags.length === 0}
          loading={addTag.isPending}
        >
          Save
        </Button>
      </div>
    </Overlay>
  );
};

export default TagOverlay;
